/**
 * Where a string filter's value suggestions come from.
 *
 * Every source sits behind one {@link SuggestionsProvider}, so the value editor
 * never knows whether its values came from an API call or were handed to it.
 * Suggestions are hints, not a closed set: whatever the user types stays a
 * valid value.
 */

/** What a provider returns for one search. */
export interface SuggestionPage {
  /** Values already matching the search, in display order. */
  values: string[];
  /** More values match than were returned. */
  truncated?: boolean;
  /**
   * Some entity in scope has no value, so "Not set" is worth offering. Left
   * out when the source can't tell, which the editor reads as "maybe".
   */
  hasUnset?: boolean;
  /**
   * How many entities hold each value (its popularity), keyed by value: the
   * editor shows it beside the value and orders the selection by it. A value
   * left out has no count to show. May hold values not on this page.
   */
  counts?: Readonly<Record<string, number>>;
  /** How many entities leave the field unset, counted like `counts`. */
  unsetCount?: number;
  /**
   * A line about the whole source, regardless of the search, ready to show
   * beside the suggestions (e.g. how many values it holds). Left out when the
   * source has nothing to say.
   */
  summary?: string;
}

/** An explicit set of values, matched client-side by case-insensitive substring. */
export interface ListSuggestionsProvider {
  kind: "list";
  values: readonly string[];
  /** See {@link SuggestionPage.hasUnset}. */
  hasUnset?: boolean;
  /** See {@link SuggestionPage.counts}. */
  counts?: Readonly<Record<string, number>>;
  /** See {@link SuggestionPage.unsetCount}. */
  unsetCount?: number;
}

/** What a list provider or page says beyond its values. */
type SuggestionExtras = Pick<
  SuggestionPage,
  "hasUnset" | "counts" | "unsetCount"
>;

/** `target` with each extra that `extras` defines, leaving the rest out. */
function withExtras<T extends SuggestionExtras>(
  target: T,
  extras: SuggestionExtras,
): T {
  if (extras.hasUnset !== undefined) {
    target.hasUnset = extras.hasUnset;
  }
  if (extras.counts !== undefined) {
    target.counts = extras.counts;
  }
  if (extras.unsetCount !== undefined) {
    target.unsetCount = extras.unsetCount;
  }
  return target;
}

/** Suggestions fetched per search, typically from an API. */
export interface FetchSuggestionsProvider {
  kind: "fetch";
  /**
   * Names the source for caching, like `optionsKey`: pages are cached per
   * `(key, search)`, so two providers sharing a key must return the same
   * page for the same search.
   */
  key: string;
  /** Resolves the page for `search`, whose values already match it. */
  fetch: (search: string, signal: AbortSignal) => Promise<SuggestionPage>;
  /**
   * Shortest non-empty search worth fetching; shorter ones keep the previous
   * page. Set it for a source whose short searches are slow (a trigram
   * `CONTAINS` scan can't use its index below 3 characters). @default 0
   */
  minSearchLength?: number;
}

/** Where one field's suggestions come from. */
export type SuggestionsProvider =
  ListSuggestionsProvider | FetchSuggestionsProvider;

/** The values containing `search`, case-insensitively, in their given order. */
export function matchSuggestions(
  values: readonly string[],
  search: string,
): string[] {
  const needle = search.trim().toLowerCase();
  return values.filter((value) => value.toLowerCase().includes(needle));
}

/** A {@link ListSuggestionsProvider} over `values`. */
export function listSuggestions(
  values: readonly string[],
  options: SuggestionExtras = {},
): ListSuggestionsProvider {
  return withExtras<ListSuggestionsProvider>({ kind: "list", values }, options);
}

/** The page `provider` offers for `search`, whichever kind it is. */
export function suggestionPage(
  provider: SuggestionsProvider,
  search: string,
  signal: AbortSignal,
): Promise<SuggestionPage> {
  if (provider.kind === "fetch") {
    return provider.fetch(search, signal);
  }
  return Promise.resolve(listPage(provider, search));
}

function listPage(
  provider: ListSuggestionsProvider,
  search: string,
): SuggestionPage {
  return withExtras<SuggestionPage>(
    { values: matchSuggestions(provider.values, search) },
    provider,
  );
}

/**
 * Whether either source has unset entities: `true` if either says so, `false`
 * only when both say not, and unknown otherwise.
 */
function eitherHasUnset(
  a: boolean | undefined,
  b: boolean | undefined,
): boolean | undefined {
  if (a === true || b === true) {
    return true;
  }
  if (a === false && b === false) {
    return false;
  }
  return undefined;
}

function dedupe(values: readonly string[]): string[] {
  return [...new Set(values)];
}

/**
 * `provider` without its counts, for a place that counts a different scope
 * than the provider does: a count from the wrong scope reads as the right one.
 * Its values, and what it says about unset ones, are kept.
 */
export function withoutCounts(
  provider: SuggestionsProvider,
): SuggestionsProvider {
  if (provider.kind === "list") {
    return listSuggestions(provider.values, { hasUnset: provider.hasUnset });
  }
  return {
    kind: "fetch",
    // Its pages differ from the counted provider's, so they're cached apart.
    key: `${provider.key}|uncounted`,
    minSearchLength: provider.minSearchLength,
    fetch: async (search, signal) => {
      const page = await provider.fetch(search, signal);
      const uncounted: SuggestionPage = { values: page.values };
      if (page.truncated !== undefined) {
        uncounted.truncated = page.truncated;
      }
      return withExtras(uncounted, { hasUnset: page.hasUnset });
    },
  };
}

/**
 * A provider listing `leading`'s values that match the search first, then
 * `provider`'s, with duplicates removed. `hasUnset` is true when either says
 * so. Used to put values already on the client (a chart's own series) ahead of
 * an org-wide search.
 *
 * Counts (the unset count with them) come from one side only: the leading
 * list's when it has any, else the provider's. The two count different scopes
 * (a chart's selection, the whole org), so mixing them would rank values
 * against each other unfairly.
 */
export function withLeading(
  leading: ListSuggestionsProvider,
  provider: SuggestionsProvider | undefined,
): SuggestionsProvider {
  if (provider === undefined) {
    return leading;
  }
  const countedBy = (other: SuggestionExtras) =>
    leading.counts === undefined ? other : leading;
  const extrasOf = (other: SuggestionExtras): SuggestionExtras => ({
    hasUnset: eitherHasUnset(leading.hasUnset, other.hasUnset),
    counts: countedBy(other).counts,
    unsetCount: countedBy(other).unsetCount,
  });

  if (provider.kind === "list") {
    // Client-side matching keeps list order, so the leading values still come
    // first after matching.
    return listSuggestions(
      dedupe([...leading.values, ...provider.values]),
      extrasOf(provider),
    );
  }

  return {
    kind: "fetch",
    // The leading values and what they say about unset values and counts
    // change the page, so they are part of what it's cached under.
    key: `${provider.key}|leading:${JSON.stringify([leading.values, leading.hasUnset ?? null, leading.counts ?? null, leading.unsetCount ?? null])}`,
    minSearchLength: provider.minSearchLength,
    fetch: async (search, signal) => {
      const page = await provider.fetch(search, signal);
      const merged: SuggestionPage = {
        values: dedupe([
          ...matchSuggestions(leading.values, search),
          ...page.values,
        ]),
      };
      if (page.truncated !== undefined) {
        merged.truncated = page.truncated;
      }
      return withExtras(merged, extrasOf(page));
    },
  };
}
